import "./InventoryChartKey.css";

import { Fragment } from "react";

import { ColorConstants, ProjectIssuanceConstants } from "../../../constants";
import { InventorySurface } from "../../../models";
import {
  getInventoryKeyColours,
  getInventoryVerifiedColours,
  getNutrientChartColours,
  SALES_OVER_TIME_SERIES,
} from "../../../utils";
import { InventoryChartKeyProps } from "./models";

const {
  ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY: BIODIVERSITY,
  ISSUANCE_UNIT_MEASUREMENT_CARBON: CARBON,
  ISSUANCE_UNIT_MEASUREMENT_ECOSYSTEM_RECOVERY: ECOSYSTEM_RECOVERY,
  ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY: NUTRIENT_NEUTRALITY,
  ISSUANCE_UNIT_MEASUREMENT_RESTORATION: RESTORATION,
} = ProjectIssuanceConstants;

/**
 * Whether a measurement's chart splits its units into verified and pending, which is what the
 * heading wording and the Pending block describe. Biodiversity never has.
 *
 * Nutrient Neutrality has no pending units at all - the measurement declares no verificationStatus
 * and Product confirmed there is no verification event - so it takes the "Units" treatment on every
 * surface that draws it (REQ-017): the project one, whose chart carries three series rather than
 * six; the asset-manager pre-access one, whose NN chart zeroes its two pending series (KAN-9745);
 * and the public one, whose NN chart stacks Allocated/Available only (KAN-9746).
 *
 * The public surface reached the same rendering before KAN-9746 by a different route - its Pending
 * block tests `=== "Carbon"`, so NN was already suppressed - which is why widening this predicate
 * changes nothing there. It is stated here so the reason is the measurement rather than a
 * coincidence of that surface's own predicate.
 *
 * Restoration (Restore 30x30) has no pending units either, so it reads "Units" with no Pending block
 * on every surface, in its own MAGENTA family. Its charts take `verifiedBy: "none"` to match.
 *
 * An unrecognised measurement still counts as split, preserving the existing fallback.
 */
const hasVerifiedPendingSplit = (unitMeasurement: string): boolean => {
  if (unitMeasurement === BIODIVERSITY) return false;
  if (unitMeasurement === NUTRIENT_NEUTRALITY) return false;
  if (unitMeasurement === RESTORATION) return false;
  return true;
};

/**
 * The heading wording. REQ-017 (the ONE deliberately removed difference on KAN-9741) resolves the
 * Ecosystem recovery divergence in which the project and asset-manager keys read "Verified" while
 * the public one read "Units" for the same project: it now reads "Verified" everywhere, because it
 * is the Wilder Carbon path and carries a real EIU (pending) / WCC (verified) split.
 *
 * That is the ONLY change. Each surface otherwise keeps its own predicate, so an unrecognised
 * measurement still reads "Verified" on project and asset-manager (which test `!== "Biodiversity"`)
 * and "Units" on public (which tests `=== "Carbon"`). Collapsing those into one rule would remove a
 * second difference, which D-1 forbids without sign-off.
 *
 * The dashboard is excluded entirely: its chart has no verified/pending split, so it has always read
 * "Units" for every measurement and continues to.
 */
const getHeading = (surface: InventorySurface, unitMeasurement: string): string => {
  if (surface === "public") {
    return unitMeasurement === CARBON || unitMeasurement === ECOSYSTEM_RECOVERY ? "Verified" : "Units";
  }
  return hasVerifiedPendingSplit(unitMeasurement) ? "Verified" : "Units";
};

/**
 * Whether the Pending block renders. Deliberately NOT unified - D-1 requires the per-surface split
 * to survive as explicit branching:
 *   - project and assetManagerPreAccess test `!== "Biodiversity"`, so an unknown measurement gets
 *     the full pending treatment;
 *   - public tests `=== "Carbon"`, so Ecosystem recovery and any unknown measurement suppress it.
 * The consequence on public + Ecosystem recovery is a "Verified" heading with no Pending block. That
 * is the specified outcome of unifying the wording while preserving the block.
 */
const showPendingBlock = (surface: InventorySurface, unitMeasurement: string): boolean => {
  switch (surface) {
    case "public":
      return unitMeasurement === CARBON;
    case "project":
    case "assetManagerPreAccess":
      return hasVerifiedPendingSplit(unitMeasurement);
    case "dashboard":
    default:
      return false;
  }
};

const KeyItem = ({ colour, label }: { colour: string; label: string }): JSX.Element => (
  <div className="KeyItem">
    <div className="KeyItemColour" style={{ backgroundColor: colour }} />
    <p>{label}</p>
  </div>
);

/**
 * One heading-plus-block per nutrient, separated by the divider the Pending block uses.
 *
 * Nutrient Neutrality colours its bars by nutrient rather than by verified/pending, so the key has
 * to as well. The palettes come from getNutrientChartColours, shared with both chart builders, so a
 * nutrient's swatch always matches its bars.
 *
 * `showAllocatedAndAvailable` mirrors the same switch on the surrounding key: the landholding chart
 * stacks Allocated/Available rather than Available/Reserved/Sold for a viewer without the full
 * figures, and its nutrient bars are no exception. Without this the key would name a Reserved colour
 * that surface never draws, and omit the Allocated one it does.
 *
 * `showAvailable` is the public surface's own gate, which suppresses the Available item for a
 * project not listed as available for sale. It defaults to true so the surfaces that have never had
 * the concept are unaffected.
 */
const NutrientKeyBlocks = ({
  nutrients,
  showAllocatedAndAvailable,
  showAvailable = true,
}: {
  nutrients: string[];
  showAllocatedAndAvailable: boolean;
  showAvailable?: boolean;
}): JSX.Element => (
  <>
    {nutrients.map((nutrient, index) => {
      const colours = getNutrientChartColours(nutrient, index);

      return (
        <Fragment key={nutrient}>
          {index > 0 && <div className="KeyBreak" />}
          <h3>{nutrient}</h3>
          {showAllocatedAndAvailable ? (
            <>
              <KeyItem colour={colours.dark} label="Allocated" />
              {showAvailable && <KeyItem colour={colours.light} label="Available" />}
            </>
          ) : (
            <>
              <KeyItem colour={colours.light} label="Available" />
              <KeyItem colour={colours.mid} label="Reserved" />
              <KeyItem colour={colours.dark} label="Sold" />
            </>
          )}
        </Fragment>
      );
    })}
  </>
);

export const InventoryChartKey = ({
  surface,
  unitMeasurement,
  showAvailable = false,
  isSalesOverTime = false,
  salesOverTimeSeries = SALES_OVER_TIME_SERIES,
  showAllocatedAndAvailable = false,
  nutrients = [],
}: InventoryChartKeyProps): JSX.Element => {
  const className = surface === "dashboard" ? "InventoryChartKey InventoryChartKey_dashboard" : "InventoryChartKey";

  /*
    Dashboard, sales-over-time: one swatch per measurement the chart draws, given the same list as
    the chart's lines so the two cannot drift.
  */
  if (surface === "dashboard" && isSalesOverTime) {
    return (
      <div className={className}>
        <h3>Total value</h3>
        {salesOverTimeSeries.map((series) => (
          <KeyItem key={series.unitMeasurement} colour={series.colour} label={series.unitMeasurement} />
        ))}
      </div>
    );
  }

  /*
    Dashboard, unit views: always "Units", never a Pending block, and the colour family swaps by
    measurement - the inverse of the other three surfaces, where verified is always GREEN.
  */
  if (surface === "dashboard") {
    /*
      Nutrient Neutrality is coloured per nutrient, not by the dashboard's measurement swap. Falls
      through to that swap when no nutrients were drawn - an empty grid, or any other measurement.
    */
    if (unitMeasurement === NUTRIENT_NEUTRALITY && nutrients.length > 0) {
      return (
        <div className={className}>
          <NutrientKeyBlocks nutrients={nutrients} showAllocatedAndAvailable={showAllocatedAndAvailable} />
        </div>
      );
    }

    const colours = getInventoryKeyColours(unitMeasurement);

    return (
      <div className={className}>
        <h3>Units</h3>
        {showAllocatedAndAvailable ? (
          <>
            <KeyItem colour={colours.dark} label="Allocated" />
            <KeyItem colour={colours.light} label="Available" />
          </>
        ) : (
          <>
            <KeyItem colour={colours.light} label="Available" />
            <KeyItem colour={colours.mid} label="Reserved" />
            <KeyItem colour={colours.dark} label="Sold" />
          </>
        )}
      </div>
    );
  }

  const isPendingBlockShown = showPendingBlock(surface, unitMeasurement);

  /*
    Project: six items, Available/Reserved/Sold in both blocks, and `showAvailable` is deliberately
    ignored - this surface has never had the concept and always renders everything.
  */
  if (surface === "project") {
    /*
      Nutrient Neutrality colours its bars by nutrient rather than by verified/pending, so the key
      does too: one heading-plus-triple block per nutrient, separated by the same KeyBreak divider
      the pending block uses. The palettes come from getNutrientChartColours, shared with the chart
      builder, so a nutrient's swatch always matches its bars.

      Falls through to the layout below when no nutrients were supplied - an NN project with no
      issuances draws no chart at all, and every other surface passes nothing.
    */
    if (unitMeasurement === NUTRIENT_NEUTRALITY && nutrients.length > 0) {
      // This surface has no Allocated/Available mode at all, so its blocks are always the triple.
      return (
        <div className={className}>
          <NutrientKeyBlocks nutrients={nutrients} showAllocatedAndAvailable={false} />
        </div>
      );
    }

    const verifiedColours = getInventoryVerifiedColours(unitMeasurement);

    return (
      <div className={className}>
        <h3>{getHeading(surface, unitMeasurement)}</h3>
        <KeyItem colour={verifiedColours.light} label="Available" />
        <KeyItem colour={verifiedColours.mid} label="Reserved" />
        <KeyItem colour={verifiedColours.dark} label="Sold" />
        {isPendingBlockShown && (
          <>
            <div className="KeyBreak" />
            <h3>Pending</h3>
            <KeyItem colour={ColorConstants.INDIGO_100} label="Available" />
            <KeyItem colour={ColorConstants.INDIGO_300} label="Reserved" />
            <KeyItem colour={ColorConstants.INDIGO_500} label="Sold" />
          </>
        )}
      </div>
    );
  }

  /*
    Public and asset-manager pre-access, Nutrient Neutrality: both charts colour their bars per
    nutrient, as the project one does, so the key names those colours rather than the shared GREEN
    pair. Both stack Allocated/Available rather than the project surface's triple, hence
    showAllocatedAndAvailable, and both keep their own showAvailable gate.

    Falls through to the layout below when no nutrients were drawn - an NN project with no issuances
    draws no chart at all, and every other measurement passes nothing.
  */
  if (unitMeasurement === NUTRIENT_NEUTRALITY && nutrients.length > 0) {
    return (
      <div className={className}>
        <NutrientKeyBlocks nutrients={nutrients} showAllocatedAndAvailable showAvailable={showAvailable} />
      </div>
    );
  }

  // public + assetManagerPreAccess: Allocated/Available, both gated on showAvailable.
  const verifiedColours = getInventoryVerifiedColours(unitMeasurement);

  return (
    <div className={className}>
      <h3>{getHeading(surface, unitMeasurement)}</h3>
      <KeyItem colour={verifiedColours.dark} label="Allocated" />
      {showAvailable && <KeyItem colour={verifiedColours.light} label="Available" />}
      {isPendingBlockShown && (
        <>
          <div className="KeyBreak" />
          <h3>Pending</h3>
          <KeyItem colour={ColorConstants.INDIGO_500} label="Allocated" />
          {showAvailable && <KeyItem colour={ColorConstants.INDIGO_100} label="Available" />}
        </>
      )}
    </div>
  );
};
